// src/data/workingCopyStore.js - RULING 305.10 (Record 647): the Firestore half of the officer's private working copy.
// Every decision is in src\data\workingCopy.js; this reads, writes and publishes. firestore.rules decides who may.
import { db } from '../firebase';
import { doc, getDoc, setDoc, deleteDoc, writeBatch, collection, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { claimPatch } from './claimWrite';
import { withCompletion, withRuleAct, publishPlan, emptyCopy } from './workingCopy';

const copyRef = (claimId, uid) => doc(db, 'claims', claimId, 'workingCopies', uid);
const stored = (c, startedAt) => ({
  holderUid: c.holderUid, holderRole: c.holderRole, completion: c.completion, ruleActs: c.ruleActs,
  startedAt: startedAt || serverTimestamp(), updatedAt: serverTimestamp(),
});

export function watchCopy(claimId, user, onCopy) {
  if (!claimId || !user || !user.uid) { onCopy(null); return () => {}; }
  return onSnapshot(copyRef(claimId, user.uid), (s) => onCopy(s.exists() ? s.data() : null), () => onCopy(null));
}

async function current(claimId, user) {
  const s = await getDoc(copyRef(claimId, user.uid));
  return s.exists() ? s.data() : null;
}

// Returns { ok, errors }.
export async function saveCompletionToCopy(claimId, user, values) {
  const now = await current(claimId, user);
  const r = withCompletion(now || emptyCopy(user), user, values);
  if (!r.ok) return r;
  await setDoc(copyRef(claimId, user.uid), stored(r.copy, now && now.startedAt));
  return { ok: true, errors: [] };
}

export async function addRuleActToCopy(claimId, claim, user, act) {
  const now = await current(claimId, user);
  const r = withRuleAct(claim, user, now || emptyCopy(user), act, new Date().toISOString());
  if (!r.ok) return r;
  await setDoc(copyRef(claimId, user.uid), stored(r.copy, now && now.startedAt));
  return { ok: true, errors: [] };
}

// Publish: the claim patch, ruling 13's notifications and the copy's deletion in ONE batch - all of it or none of it.
// The plan is computed against the claim as the caller read it now (pass the fresh claim).
export async function publishCopy(claimId, claim, user) {
  const now = await current(claimId, user);
  if (!now) return { ok: false, errors: ['There is nothing in your working copy.'] };
  const p = publishPlan(claim, user, now, new Date().toISOString());
  if (!p.ok) return { ok: false, errors: p.errors };
  const batch = writeBatch(db);
  if (p.changed) batch.update(doc(db, 'claims', claimId), claimPatch({ ...p.patch, updatedAt: serverTimestamp() }, user));
  p.notifications.forEach((n) => batch.set(doc(collection(db, 'notifications')), { ...n, createdAt: serverTimestamp() }));
  batch.delete(copyRef(claimId, user.uid));
  await batch.commit();
  return { ok: true, errors: [], patch: p.patch };
}

export async function discardCopy(claimId, user) {
  await deleteDoc(copyRef(claimId, user.uid));
}
